import { type ReactNode, useCallback } from "react";

import { useShallow } from "zustand/react/shallow";

import type { ModalBehaviorOptions, ModalCloseSource } from "./modal-behavior";
import { ModalView } from "./modal-view";
import { useUserTriggeredModalsStore } from "./user-triggered-modals.store";

interface UserTriggeredModalViewProps {
  id: string;
  depth: number;
  onContentResize: () => void;
  onStateChange: (id: string, value: unknown) => void;
  onClose: (source: ModalCloseSource) => void;
  renderStackOverlay: ReactNode;
}

export const UserTriggeredModalView = ({ id, ...props }: UserTriggeredModalViewProps) => {
  const children = useUserTriggeredModalsStore(useShallow((state) => state.children[id]));
  const args = useUserTriggeredModalsStore(useShallow((state) => state.args[id]));
  const peekState = useUserTriggeredModalsStore(useShallow((state) => state.peekState));
  const hasCloseButton = useUserTriggeredModalsStore((state) => state.behaviorOptions[id]?.hasCloseButton);

  const setBehaviorOptions = useUserTriggeredModalsStore((state) => state.setBehaviorOptions);

  const onBehaviorChange = useCallback(
    (value: ModalBehaviorOptions) => {
      setBehaviorOptions(id, value);
    },
    [id, setBehaviorOptions],
  );

  const onPeekState = useCallback(() => peekState(id), [peekState, id]);

  const onStateChange = useCallback(
    (value: unknown) => {
      props.onStateChange(id, value);
    },
    [props.onStateChange, id],
  );

  return (
    <ModalView
      {...props}
      args={args}
      hasCloseButton={hasCloseButton}
      onBehaviorChange={onBehaviorChange}
      onPeekState={onPeekState}
      onStateChange={onStateChange}
    >
      {children}
    </ModalView>
  );
};
